import { useMemo } from 'react';
import { EntityLabel, useUserLabels } from '../../hooks/use-entity-labels.hook';
import { Paths, ProjectPathParams, createPath } from '../router/app.router';
import { NavigationItem } from '../../ui/components/pure/navigation-bar/navigation-bar.component';
import { useDisplayWorkflowTypes } from '../../hooks/use-display-workflow-types.hook';
import { KnownProjectDtoDisplayWorkflowTypesItem } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useQueryGetProjectOptional } from '../../core/queries/projects.queries';
import { isCarrierProjectType, isNetworkProjectType } from './courier-project-type';
import { All } from '../../ui/components/pure/icons/All.icon';
import { Order } from '../../ui/components/pure/icons/Order.icon';

export function useNavigationItems(params: Partial<ProjectPathParams<Paths.project>> | undefined) {
  const userLabel = useUserLabels(params?.projectID);
  const project = useQueryGetProjectOptional(params?.projectID);
  const [displayWorkflowTypes, isLoading] = useDisplayWorkflowTypes(params?.projectID || '');

  return useMemo(
    () =>
      makeNavigationItems(
        displayWorkflowTypes,
        isLoading || project.isLoading,
        params,
        userLabel,
        project.data?.projectType
      ),
    [displayWorkflowTypes, isLoading, params, userLabel, project.isLoading, project.data?.projectType]
  );
}

function makeNavigationItems(
  displayWorkflowTypes: string[],
  isLoading: boolean,
  params?: Partial<ProjectPathParams<Paths.project>>,
  userLabel?: EntityLabel,
  projectType?: string
): NavigationItem[] {
  // A carrier or region project has no lockers, so no hubs, dashboard or reports read against it.
  const isCarrier = isCarrierProjectType(projectType);
  // A network manages carriers, not sites, so it drops the site-level items.
  const isNetwork = isNetworkProjectType(projectType);

  return params?.projectID && !isLoading
    ? [
        ...(isCarrier
          ? []
          : [
              {
                text: 'Dashboard',
                url: createPath(Paths.dashboard, { projectID: params.shortcode || params.projectID }),
                permissions: [
                  {
                    action: 'deliveries:List',
                    projectID: params.projectID
                  }
                ]
              }
            ]),
        ...(displayWorkflowTypes.length > 1
          ? [
              {
                text: 'Deliveries',
                children: [
                  {
                    text: 'All',
                    icon: All,
                    url: createPath(Paths.deliveries, { projectID: params.shortcode || params.projectID }),
                    permissions: [
                      {
                        action: 'deliveries:List',
                        projectID: params.projectID
                      }
                    ]
                  },
                  {
                    text: 'Internal View',
                    icon: All,
                    url: createPath(Paths.deliveriesInternal, { projectID: params.shortcode || params.projectID }),
                    permissions: [
                      {
                        action: 'deliveries:List',
                        projectID: params.projectID
                      }
                    ],
                    availableUiFeatures: ['internal-groundfloor']
                  },
                  {
                    text: 'Orders',
                    icon: Order,
                    url: createPath(Paths.orders, { projectID: params.shortcode || params.projectID }),
                    permissions: [
                      {
                        action: 'deliveries:List',
                        projectID: params.projectID
                      }
                    ]
                  }
                ]
              }
            ]
          : [
              ...(displayWorkflowTypes.includes(KnownProjectDtoDisplayWorkflowTypesItem.WorkflowDelivery)
                ? [
                    {
                      text: 'Deliveries',
                      children: [
                        {
                          text: 'All',
                          icon: All,
                          url: createPath(Paths.deliveries, { projectID: params.shortcode || params.projectID }),
                          permissions: [
                            {
                              action: 'deliveries:List',
                              projectID: params.projectID
                            }
                          ]
                        },
                        {
                          text: 'Internal View',
                          icon: All,
                          url: createPath(Paths.deliveriesInternal, {
                            projectID: params.shortcode || params.projectID
                          }),
                          permissions: [
                            {
                              action: 'deliveries:List',
                              projectID: params.projectID
                            }
                          ],
                          availableUiFeatures: ['internal-groundfloor']
                        }
                      ]
                    }
                  ]
                : []),

              ...(displayWorkflowTypes.includes(KnownProjectDtoDisplayWorkflowTypesItem.WorkflowRetail)
                ? [
                    {
                      text: 'Orders',
                      url: createPath(Paths.orders, { projectID: params.shortcode || params.projectID }),
                      permissions: [
                        {
                          action: 'deliveries:List',
                          projectID: params.projectID
                        }
                      ]
                    }
                  ]
                : [])
            ]),

        ...(isCarrier
          ? [
              {
                // useUserLabels resolves to Couriers for carrier and region projects
                text: userLabel?.plural || 'Couriers',
                url: createPath(Paths.couriers, { projectID: params.shortcode || params.projectID }),
                permissions: [
                  {
                    action: 'users:List',
                    projectID: params.projectID
                  }
                ]
              }
            ]
          : isNetwork
          ? // No users of its own — useUserLabels falls through to "Users" here, which says it.
            []
          : [
              {
                text: userLabel?.plural || 'Customers',
                url: createPath(Paths.customers, { projectID: params.shortcode || params.projectID }),
                permissions: [
                  {
                    action: 'users:List',
                    projectID: params.projectID
                  }
                ]
              }
            ]),
        {
          text: 'Team',
          url: createPath(Paths.team, { projectID: params.shortcode || params.projectID }),
          permissions: [
            {
              action: 'projects:ListTeamMembers',
              projectID: params.projectID
            }
          ]
        },
        ...(isCarrier || isNetwork
          ? []
          : [
              {
                text: 'Hubs',
                url: createPath(Paths.hubs, { projectID: params.shortcode || params.projectID }),
                permissions: [
                  {
                    action: 'hubs:ListDetails',
                    projectID: params.projectID
                  }
                ]
              }
            ]),
        ...(isCarrier
          ? []
          : [
              {
                text: 'Reports',
                availableUiFeatures: ['internal-groundfloor'],
                children: [
                  {
                    text: 'Delivery Container Utilisation',
                    url: createPath(Paths.deliveryContainerUtilisationReport, {
                      projectID: params.shortcode || params.projectID
                    }),
                    availableUiFeatures: ['experimental'],
                    permissions: [
                      {
                        action: 'hubs:ListDetails',
                        projectID: params.projectID
                      }
                    ]
                  },
                  {
                    text: 'Legacy Delivery Container Utilisation',
                    url: createPath(Paths.lockerUtilisationReport, { projectID: params.shortcode || params.projectID }),
                    permissions: [
                      {
                        action: 'hubs:ListDetails',
                        projectID: params.projectID
                      }
                    ]
                  },
                  {
                    text: 'Deliveries Requiring Attention',
                    url: createPath(Paths.deliveriesRequiringAttention, {
                      projectID: params.shortcode || params.projectID
                    }),
                    permissions: [
                      {
                        action: 'deliveries:List',
                        projectID: params.projectID
                      }
                    ]
                  },
                  {
                    text: 'Courier Login Testing',
                    url: createPath(Paths.courierLoginTesting, { projectID: params.shortcode || params.projectID }),
                    availableUiFeatures: ['internal-groundfloor']
                  },
                  {
                    text: 'Kiosk Status',
                    url: createPath(Paths.kioskConnectionStatusReport, {
                      projectID: params.shortcode || params.projectID
                    }),
                    availableUiFeatures: ['internal-groundfloor']
                  }
                ]
              }
            ]),
        {
          // A menu on every project, so Settings doesn't change shape between projects.
          text: 'Settings',
          permissions: [
            {
              action: 'projects:List',
              projectID: params.projectID
            }
          ],
          availableUiFeatures: ['internal-groundfloor'],
          children: [
            {
              text: 'Project Settings',
              url: createPath(Paths.settings, { projectID: params.shortcode || params.projectID }),
              permissions: [
                {
                  action: 'projects:List',
                  projectID: params.projectID
                }
              ]
            },
            {
              text: 'Carriers',
              availableUiFeatures: ['internal-groundfloor'],
              children: [
                {
                  text: 'All Carriers',
                  url: createPath(Paths.carriers, { projectID: params.shortcode || params.projectID }),
                  permissions: [
                    {
                      action: 'groups:List',
                      projectID: params.projectID
                    }
                  ]
                },
                {
                  text: 'Pending Carriers',
                  url: createPath(Paths.pendingCarriers, { projectID: params.shortcode || params.projectID }),
                  permissions: [
                    {
                      action: 'groups:List',
                      projectID: params.projectID
                    }
                  ]
                }
              ]
            },
            {
              text: 'Couriers',
              availableUiFeatures: ['internal-groundfloor'],
              children: [
                {
                  text: 'Pending Couriers',
                  url: createPath(Paths.pendingCouriers, { projectID: params.shortcode || params.projectID }),
                  permissions: [
                    {
                      action: 'users:List',
                      projectID: params.projectID
                    }
                  ]
                },
                {
                  text: 'Incomplete Couriers',
                  url: createPath(Paths.incompleteCouriers, { projectID: params.shortcode || params.projectID }),
                  permissions: [
                    {
                      action: 'users:List',
                      projectID: params.projectID
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    : [];
}
